import React, { useState, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { FaAndroid, FaApple } from 'react-icons/fa';
import { FiBell, FiSmartphone } from 'react-icons/fi';

const AppLinksModal = ({ isOpen, onClose, app, onUpdateLinks }) => {
  const [playStoreUrl, setPlayStoreUrl] = useState('');
  const [appStoreUrl, setAppStoreUrl] = useState('');
  const [onesignalUrl, setOnesignalUrl] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const { isDarkMode } = useTheme();

  useEffect(() => {
    if (app) {
      setPlayStoreUrl(app.playStoreUrl || '');
      setAppStoreUrl(app.appStoreUrl || '');
      setOnesignalUrl(app.onesignalUrl || '');
    }
  }, [app]);

  if (!isOpen || !app) return null;

  const handleSave = async () => {
    setIsSaving(true);
    setErrorMsg(null);
    try {
      await onUpdateLinks(app.id, {
        playStoreUrl,
        appStoreUrl,
        onesignalUrl,
      });
      onClose();
    } catch (error) {
      console.error("Erro ao salvar os links:", error);
      setErrorMsg("Ocorreu um erro ao salvar. Verifique sua conexão e tente novamente.");
    } finally {
      setIsSaving(false);
    }
  };

  const modalBg = isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100';
  const textColor = isDarkMode ? 'text-white' : 'text-gray-900';
  const textColorSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const borderColor = isDarkMode ? 'border-gray-800' : 'border-gray-100';
  const inputBg = isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900';
  const footerBg = isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-gray-50/80 border-gray-100 backdrop-blur-md';
  const buttonClose = isDarkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-800' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100';

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 sm:p-6">
      <div className={`rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col border ${modalBg} overflow-hidden`}>
        {/* Header */}
        <div className={`flex justify-between items-center px-6 py-5 border-b ${borderColor} ${modalBg}`}>
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
              <FiSmartphone className={`w-5 h-5 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
            </div>
            <div>
              <h3 className={`text-lg font-semibold ${textColor}`}>Links e Lojas do App</h3>
              <p className={`text-sm ${textColorSecondary}`}>{app.name || 'Aplicativo'}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`transition-colors p-2 rounded-md ${buttonClose}`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 overflow-y-auto">
          {errorMsg && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400 p-4 rounded-xl flex items-center gap-3 text-sm">
              <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <span>{errorMsg}</span>
            </div>
          )}

          {/* Play Store */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${textColor}`}>
              <div className="flex items-center gap-2">
                <FaAndroid className="text-green-500" /> Google Play Store URL
              </div>
            </label>
            <input
              type="url"
              value={playStoreUrl}
              onChange={(e) => setPlayStoreUrl(e.target.value)}
              placeholder="Ex: https://play.google.com/store/apps/details?id=com.seuapp"
              className={`w-full px-4 py-2.5 rounded-xl border focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 transition-colors ${inputBg}`}
            />
          </div>

          {/* App Store */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${textColor}`}>
              <div className="flex items-center gap-2">
                <FaApple className="text-blue-500" /> Apple App Store URL
              </div>
            </label>
            <input
              type="url"
              value={appStoreUrl}
              onChange={(e) => setAppStoreUrl(e.target.value)}
              placeholder="Ex: https://apps.apple.com/br/app/seu-app/id123456789"
              className={`w-full px-4 py-2.5 rounded-xl border focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 transition-colors ${inputBg}`}
            />
          </div>

          {/* OneSignal */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${textColor}`}>
              <div className="flex items-center gap-2">
                <FiBell className="text-purple-500" /> OneSignal URL (Notificações)
              </div>
            </label>
            <input
              type="url"
              value={onesignalUrl}
              onChange={(e) => setOnesignalUrl(e.target.value)}
              placeholder="URL do painel do OneSignal"
              className={`w-full px-4 py-2.5 rounded-xl border focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 transition-colors ${inputBg}`}
            />
            <p className={`mt-2 text-xs ${textColorSecondary}`}>
              Essa URL permite que o cliente acesse o painel de push notifications diretamente.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className={`px-6 py-4 border-t flex justify-end gap-3 ${footerBg} ${borderColor}`}>
          <button
            onClick={onClose}
            className={`px-4 py-2.5 text-sm font-medium rounded-xl transition-colors ${isDarkMode
                ? 'text-gray-300 hover:bg-gray-800'
                : 'text-gray-700 hover:bg-gray-100'
              }`}
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-6 py-2.5 text-sm font-medium bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl shadow-sm transition-all focus:ring-2 focus:ring-indigo-500/50 disabled:opacity-50 flex items-center gap-2"
          >
            {isSaving ? (
              <>
                <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Salvando...
              </>
            ) : (
              'Salvar Alterações'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AppLinksModal;
